import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, interval } from 'rxjs';
import { environment } from 'src/environments/environment';

const BACKEND_URL = environment.apiUrl + '/wall/';
const WALL_STATUS_POLL_MS = 5 * 60 * 1000;

const EXPRESS_WALL_CDN = 'https://img-express.eazix.io';

/** Missing thumbs and the box-icon fallback must not appear on the wall. */
export function isWallPlaceholderThumb(url?: string | null): boolean {
  const u = String(url || '').trim().toLowerCase().replace(/\\/g, '/');
  return !u || u.includes('box-icon.svg');
}

const WALL_COMMENT_TZ = 'Asia/Jerusalem';

/**
 * Comment timestamp label (Israel time): `HH:mm` today, else `dd/mm/yy HH:mm`.
 * Shared by exhibition + personal wall so both stay identical.
 */
export function wallCommentTimeLabel(
  created?: string | Date | null
): string {
  if (created == null || created === '') {
    return '';
  }
  const d = created instanceof Date ? created : new Date(created);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const pad = (n: string | number) => String(n).padStart(2, '0');
  const partsOf = (value: Date) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: WALL_COMMENT_TZ,
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(value);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value || '';
    return {
      day: get('day'),
      month: get('month'),
      year: get('year'),
      hour: get('hour'),
      minute: get('minute'),
    };
  };

  const comment = partsOf(d);
  const time = `${pad(comment.hour)}:${pad(comment.minute)}`;

  const dayKey = (value: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: WALL_COMMENT_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(value);

  if (dayKey(d) === dayKey(new Date())) {
    return time;
  }
  return `${pad(comment.day)}/${pad(comment.month)}/${pad(comment.year)} ${time}`;
}

function isPlaceholderProfileUrl(url: string): boolean {
  const u = url.toLowerCase();
  return (
    u.includes('default.png') ||
    u.includes('profile/default') ||
    u.includes('box-icon.svg')
  );
}

function isOauthHostedProfileUrl(url: string): boolean {
  const u = url.toLowerCase();
  return (
    u.includes('googleusercontent.com') ||
    u.includes('google.com/a/') ||
    u.includes('graph.facebook.com') ||
    u.includes('fbcdn.net')
  );
}

/** Profile-image URLs to try: uploaded photo, then Google/Facebook (same sources as the header). */
export function wallProfileImageCandidates(user: any): string[] {
  if (!user) {
    return [];
  }
  const seen = new Set<string>();
  const add = (raw: unknown) => {
    const u = String(raw || '').trim();
    if (!u || isPlaceholderProfileUrl(u) || seen.has(u)) {
      return;
    }
    seen.add(u);
  };
  const direct = String(user.profileImageURL || '').trim();
  if (direct && !isOauthHostedProfileUrl(direct)) {
    add(direct);
  }
  const providers = Array.isArray(user.provider)
    ? user.provider
    : [user.provider];
  const data = user.providerData || {};
  if (providers.includes('facebook') && data.id) {
    add(`https://graph.facebook.com/${data.id}/picture?type=large`);
  }
  add(data.picture);
  add(direct);
  return [...seen];
}

export function resolveWallProfileImage(user: any): string {
  return wallProfileImageCandidates(user)[0] || '';
}

export function nextWallProfileImage(
  current?: string | null,
  candidates?: string[] | null
): string {
  const list = (candidates || [])
    .map((u) => String(u || '').trim())
    .filter(Boolean);
  const cur = String(current || '').trim();
  const idx = list.indexOf(cur);
  const rest = idx >= 0 ? list.slice(idx + 1) : list.filter((u) => u !== cur);
  return rest[0] || '';
}

const WALL_OPEN_SCROLL_OFFSET_PX = 5;
const WALL_OPEN_SCROLL_MS = 260;
const WALL_OPEN_SCROLL_WITH_COLLAPSE_MS = 380;

let wallOpenScrollRaf = 0;
let wallOpenScrollGen = 0;

export function wallScrollParent(el: HTMLElement): HTMLElement {
  let fallback: HTMLElement | null = null;
  let sidenav: HTMLElement | null = null;
  let wallHost: HTMLElement | null = null;
  // Include `el` itself — wall hosts are the scroll container under the navbar.
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    const tag = node.tagName;
    const oy = window.getComputedStyle(node).overflowY;
    const canScroll = oy === 'auto' || oy === 'scroll' || oy === 'overlay';
    const isWallHost =
      node.classList.contains('personal-wall-host') ||
      node.classList.contains('eazix-exhibition-host');
    if (canScroll) {
      if (isWallHost && !wallHost) {
        wallHost = node;
      }
      if (node.scrollHeight > node.clientHeight + 1) {
        // Prefer the wall host even when an ancestor also scrolls.
        return wallHost || node;
      }
      if (!fallback) {
        fallback = node;
      }
    }
    if (
      !sidenav &&
      (tag === 'MAT-SIDENAV-CONTENT' ||
        node.classList.contains('mat-drawer-content')) &&
      canScroll
    ) {
      sidenav = node;
    }
    node = node.parentElement;
  }
  return (
    wallHost ||
    fallback ||
    sidenav ||
    (document.scrollingElement as HTMLElement) ||
    document.documentElement
  );
}

const WALL_SCROLL_COLLAPSE_PX = 60;

export function isWallDesktopViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth > 640;
}

/** Comment author names in the enlarged card. */
export function wallCommentAuthorNameMax(): number {
  return isWallDesktopViewport() ? 70 : 40;
}

export function isWallCommentAuthorNameTruncated(name?: string): boolean {
  return String(name || '').trim().length > wallCommentAuthorNameMax();
}

export function wallCommentAuthorDisplayName(name?: string): string {
  const n = String(name || '').trim();
  const max = wallCommentAuthorNameMax();
  if (n.length <= max) {
    return n;
  }
  return `${n.slice(0, max)}...`;
}

const WALL_COMMENT_CARD_MAX_LINES = 4;

/** Approx. characters that fit one rendered comment line. */
function wallCommentCardCharsPerLine(): number {
  return isWallDesktopViewport() ? 42 : 28;
}

/** Visual line count: wrapping of continuous text plus explicit line breaks. */
export function wallCommentCardVisualLineCount(text?: string): number {
  const per = Math.max(1, wallCommentCardCharsPerLine());
  const lines = String(text ?? '').split(/\r?\n/);
  if (!lines.length) {
    return 0;
  }
  let n = 0;
  for (const line of lines) {
    n += Math.max(1, Math.ceil(line.length / per) || 1);
  }
  return n;
}

/** Whether the comment would exceed 4 visual lines (wrap or line breaks). */
export function isWallCommentCardTextTruncated(text?: string): boolean {
  return wallCommentCardVisualLineCount(text) > WALL_COMMENT_CARD_MAX_LINES;
}

/**
 * Truncate card comment preview to 4 visual lines (wrapping and line breaks).
 * Prefer CSS clamp in the UI; this is a string fallback.
 */
export function truncateWallCommentCardText(text?: string): string {
  const raw = String(text ?? '');
  if (!raw || !isWallCommentCardTextTruncated(raw)) {
    return raw;
  }
  const per = Math.max(1, wallCommentCardCharsPerLine());
  const lines = raw.split(/\r?\n/);
  const kept: string[] = [];
  let used = 0;
  for (const line of lines) {
    const need = Math.max(1, Math.ceil(line.length / per) || 1);
    if (used + need > WALL_COMMENT_CARD_MAX_LINES) {
      const remain = WALL_COMMENT_CARD_MAX_LINES - used;
      if (remain > 0) {
        kept.push(line.slice(0, remain * per));
      }
      break;
    }
    kept.push(line);
    used += need;
  }
  const t = kept.join('\n').replace(/\s+$/u, '');
  return t.endsWith('...') ? t : `${t}...`;
}

/** Display width/height after optional 90/270 rotation. */
export function wallDisplayedSize(
  natW?: number,
  natH?: number,
  rotation?: number
): { w: number; h: number } {
  const w = Number(natW) || 0;
  const h = Number(natH) || 0;
  const r = Number(rotation) || 0;
  const sideways = r === 90 || r === 270;
  return sideways ? { w: h, h: w } : { w, h };
}

const wallImageSizeProbeCache = new Map<
  string,
  Promise<{ natW: number; natH: number } | null>
>();

/** Decode thumb natural size before collage layout (avoids 0.8→real aspect jump). */
export function probeWallImageNaturalSize(
  url?: string | null
): Promise<{ natW: number; natH: number } | null> {
  const u = toPublicWallThumb(url);
  if (!u || typeof Image === 'undefined') {
    return Promise.resolve(null);
  }
  const cached = wallImageSizeProbeCache.get(u);
  if (cached) {
    return cached;
  }
  const pending = new Promise<{ natW: number; natH: number } | null>((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    const finish = () => {
      const natW = img.naturalWidth || 0;
      const natH = img.naturalHeight || 0;
      resolve(natW > 0 && natH > 0 ? { natW, natH } : null);
    };
    img.onload = finish;
    img.onerror = () => resolve(null);
    img.src = u;
  });
  wallImageSizeProbeCache.set(u, pending);
  return pending;
}

/** Fill missing natW/natH on items (mutates in place). */
export async function hydrateWallItemImageSizes<
  T extends { thumbUrl?: string; natW?: number; natH?: number; imgLoaded?: boolean }
>(items: T[]): Promise<void> {
  const targets = (items || []).filter(
    (item) => !(Number(item.natW) > 0 && Number(item.natH) > 0)
  );
  if (!targets.length) {
    return;
  }
  await Promise.all(
    targets.map(async (item) => {
      const dims = await probeWallImageNaturalSize(item.thumbUrl);
      if (dims) {
        item.natW = dims.natW;
        item.natH = dims.natH;
      }
    })
  );
}

/**
 * Probe thumbs, drop placeholders / failed loads, mark survivors loaded.
 * Use this before updating wall tab counts.
 */
export async function filterWallItemsWithLoadableThumbs<
  T extends { thumbUrl?: string; natW?: number; natH?: number; imgLoaded?: boolean }
>(items: T[]): Promise<T[]> {
  const list = items || [];
  await hydrateWallItemImageSizes(list);
  const survivors = list.filter(
    (item) =>
      !isWallPlaceholderThumb(item.thumbUrl) &&
      Number(item.natW) > 0 &&
      Number(item.natH) > 0
  );
  for (const item of survivors) {
    item.imgLoaded = true;
  }
  return survivors;
}

/** True when displayed aspect changed enough to warrant a collage relayout. */
export function wallDisplayedAspectChanged(
  prevW?: number,
  prevH?: number,
  nextW?: number,
  nextH?: number,
  rotation?: number,
  minRelDelta = 0.02
): boolean {
  const prev = wallDisplayedSize(prevW, prevH, rotation);
  const next = wallDisplayedSize(nextW, nextH, rotation);
  if (!(next.w > 0 && next.h > 0)) {
    return false;
  }
  if (!(prev.w > 0 && prev.h > 0)) {
    return true;
  }
  const a = prev.w / prev.h;
  const b = next.w / next.h;
  return Math.abs(a - b) / a > minRelDelta;
}

const WALL_FOCUS_MEDIA_SEL =
  '.eazix-exhibition-card-media, .personal-wall-card-media';

export function wallFocusMediaEl(panel: HTMLElement): HTMLElement | null {
  return panel.querySelector(WALL_FOCUS_MEDIA_SEL) as HTMLElement | null;
}

/** Visible image box inside an enlarged card (ignores side gutters). */
export function wallFocusMediaBox(panel: HTMLElement): DOMRect {
  const media = wallFocusMediaEl(panel);
  const r = media?.getBoundingClientRect();
  if (r && r.width > 2 && r.height > 2) {
    return r;
  }
  return panel.getBoundingClientRect();
}

/**
 * Invert transform that maps the image onto `slot`, so FLIP does not squash
 * portrait photos that sit in a wider card with white side gutters.
 */
export function wallFocusMediaToSlotTransform(
  panel: HTMLElement,
  slot: { left: number; top: number; width: number; height: number }
): string {
  const panelRect = panel.getBoundingClientRect();
  const box = wallFocusMediaBox(panel);
  const mx = box.left - panelRect.left;
  const my = box.top - panelRect.top;
  const sx = slot.width / Math.max(1, box.width);
  const sy = slot.height / Math.max(1, box.height);
  const dx = slot.left - panelRect.left - mx * sx;
  const dy = slot.top - panelRect.top - my * sy;
  return `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
}

export function wallFocusPinPanelToBox(panel: HTMLElement, box: DOMRect): void {
  panel.style.right = 'auto';
  panel.style.bottom = 'auto';
  panel.style.left = `${box.left}px`;
  panel.style.top = `${box.top}px`;
  panel.style.width = `${box.width}px`;
  panel.style.maxWidth = `${box.width}px`;
  panel.style.setProperty('height', `${box.height}px`, 'important');
  panel.style.maxHeight = `${box.height}px`;
}

export function wallFocusPinMediaBox(media: HTMLElement, box: DOMRect): void {
  media.style.width = `${box.width}px`;
  media.style.maxWidth = `${box.width}px`;
  media.style.setProperty('height', `${box.height}px`, 'important');
  media.style.maxHeight = `${box.height}px`;
  media.style.aspectRatio = 'auto';
  media.style.marginInline = '0';
  media.style.flex = '0 0 auto';
}

export function wallFocusMediaFillPanel(media: HTMLElement): void {
  media.style.width = '100%';
  media.style.maxWidth = 'none';
  media.style.setProperty('height', '100%', 'important');
  media.style.maxHeight = 'none';
  media.style.aspectRatio = 'auto';
  media.style.marginInline = '0';
}

export function wallFocusClearMediaFlipStyles(panel: HTMLElement): void {
  const media = wallFocusMediaEl(panel);
  if (!media) {
    return;
  }
  media.style.width = '';
  media.style.maxWidth = '';
  media.style.removeProperty('height');
  media.style.maxHeight = '';
  media.style.aspectRatio = '';
  media.style.marginInline = '';
  media.style.alignSelf = '';
  media.style.flex = '';
  media.style.transition = '';
}

export type WallFocusFlipRadiusOpts = {
  slotRadius?: string;
  openRadius?: string;
};

/** Close: animate panel + media boxes back to gallery slot; image stays visible throughout. */
export function wallFocusPlayCloseToSlot(
  panel: HTMLElement,
  slot: { left: number; top: number; width: number; height: number },
  durationMs: number,
  returningClass: string,
  mediaStart?: DOMRect | null,
  panelStart?: DOMRect | null,
  radiusOpts?: WallFocusFlipRadiusOpts
): boolean {
  const slotRadius = radiusOpts?.slotRadius ?? '13px';
  const openRadius = radiusOpts?.openRadius ?? wallFocusOpenBorderRadius();
  if (slot.width < 2 || slot.height < 2) {
    return false;
  }

  const media = wallFocusMediaEl(panel);
  const mediaFirst =
    mediaStart && mediaStart.width > 2 && mediaStart.height > 2
      ? mediaStart
      : null;
  const panelFull =
    panelStart && panelStart.width > 2 && panelStart.height > 2
      ? panelStart
      : panel.getBoundingClientRect();
  if (panelFull.width < 2 || panelFull.height < 2) {
    return false;
  }

  // Fly back the image area only — not the full comments stack height.
  const panelFirst = mediaFirst
    ? new DOMRect(
        panelFull.left,
        mediaFirst.top,
        panelFull.width,
        mediaFirst.height
      )
    : panelFull;
  const slotRect = new DOMRect(slot.left, slot.top, slot.width, slot.height);
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const boxEase = [
    `left ${durationMs}ms ${ease}`,
    `top ${durationMs}ms ${ease}`,
    `width ${durationMs}ms ${ease}`,
    `height ${durationMs}ms ${ease}`,
    `max-width ${durationMs}ms ${ease}`,
    `max-height ${durationMs}ms ${ease}`,
    `border-radius ${durationMs}ms ${ease}`,
  ].join(', ');
  const mediaEase = [
    `width ${durationMs}ms ${ease}`,
    `max-width ${durationMs}ms ${ease}`,
    `height ${durationMs}ms ${ease}`,
    `max-height ${durationMs}ms ${ease}`,
    `margin ${durationMs}ms ${ease}`,
  ].join(', ');

  panel.style.transition = 'none';
  panel.style.transform = 'none';
  panel.style.transformOrigin = 'top left';
  panel.classList.add(returningClass);
  wallFocusPinPanelToBox(panel, panelFirst);
  panel.style.borderRadius = openRadius;
  if (media) {
    media.style.transition = 'none';
    media.style.marginInline = '0';
    media.style.flex = '0 0 auto';
    media.style.aspectRatio = 'auto';
    wallFocusPinMediaBox(media, panelFirst);
  }
  void panel.offsetWidth;

  panel.style.transition = boxEase;
  wallFocusPinPanelToBox(panel, slotRect);
  panel.style.borderRadius = slotRadius;
  if (media) {
    media.style.transition = mediaEase;
    wallFocusPinMediaBox(media, slotRect);
  }
  return true;
}

/**
 * Open: animate the focus panel from the gallery slot to the final box.
 * Media stays on normal focus CSS (centered aspect box) so it never collapses
 * when inline pin styles are cleared after the animation.
 */
export function wallFocusPlayOpenFromSlot(
  panel: HTMLElement,
  slot: { left: number; top: number; width: number; height: number },
  durationMs: number,
  openingClass: string,
  panelLast: DOMRect,
  _mediaLast?: DOMRect | null,
  radiusOpts?: WallFocusFlipRadiusOpts
): boolean {
  const slotRadius = radiusOpts?.slotRadius ?? '13px';
  const openRadius = radiusOpts?.openRadius ?? wallFocusOpenBorderRadius();
  if (slot.width < 2 || slot.height < 2) {
    return false;
  }
  if (panelLast.width < 2 || panelLast.height < 2) {
    return false;
  }

  const slotRect = new DOMRect(slot.left, slot.top, slot.width, slot.height);
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const boxEase = [
    `left ${durationMs}ms ${ease}`,
    `top ${durationMs}ms ${ease}`,
    `width ${durationMs}ms ${ease}`,
    `height ${durationMs}ms ${ease}`,
    `max-width ${durationMs}ms ${ease}`,
    `max-height ${durationMs}ms ${ease}`,
    `border-radius ${durationMs}ms ${ease}`,
  ].join(', ');

  panel.style.transition = 'none';
  panel.style.transform = 'none';
  panel.style.transformOrigin = 'top left';
  panel.classList.add(openingClass);
  wallFocusPinPanelToBox(panel, slotRect);
  // Start like the gallery tile, then ease into sheet / dialog radius.
  panel.style.borderRadius = slotRadius;
  void panel.offsetWidth;

  panel.style.transition = boxEase;
  wallFocusPinPanelToBox(panel, panelLast);
  panel.style.borderRadius = openRadius;
  return true;
}

function wallFocusIsMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= 640;
}

/** Desktop: full 16px. Mobile sheet: keep top radius, flatten bottom corners. */
function wallFocusOpenBorderRadius(): string {
  return wallFocusIsMobileViewport() ? '16px 16px 0 0' : '16px';
}

/** True when displayed height is under 50% of displayed width (very wide image). */
export function isWallImageWideShort(
  natW?: number,
  natH?: number,
  rotation?: number
): boolean {
  const { w, h } = wallDisplayedSize(natW, natH, rotation);
  return w > 0 && h > 0 && h < w * 0.5;
}

/** True when displayed width is more than 180% of displayed height. */
export function isWallImagePanoramic(
  natW?: number,
  natH?: number,
  rotation?: number
): boolean {
  const { w, h } = wallDisplayedSize(natW, natH, rotation);
  return w > 0 && h > 0 && w > h * 1.8;
}

/**
 * Max chars for overlay author/academy names on very wide images
 * (height < 50% of width / width > 200% of height).
 * Shorter on mobile so status icons fit on the same row.
 */
export function wallOverlayNameClipMax(
  natW?: number,
  natH?: number,
  rotation?: number
): number | null {
  if (isWallImageWideShort(natW, natH, rotation)) {
    return isWallDesktopViewport() ? 16 : 5;
  }
  return null;
}

/** Clip overlay names; when `max` exceeded appends "...". */
export function clipWallOverlayName(name?: string, max = 12): string {
  const n = String(name || '').trim();
  if (!n) {
    return '';
  }
  if (n.length <= max) {
    return n;
  }
  return `${n.slice(0, max)}...`;
}

/** Listen on the wall scroll container (+ window fallback) and report collapse state. */
export function bindWallScrollCollapse(
  el: HTMLElement,
  onChange: (scrolled: boolean) => void,
  threshold = WALL_SCROLL_COLLAPSE_PX,
): () => void {
  const scroller = wallScrollParent(el);
  let last = false;

  const readScrolled = (): boolean => {
    const doc = document.documentElement;
    return (
      scroller.scrollTop > threshold ||
      window.scrollY > threshold ||
      doc.scrollTop > threshold
    );
  };

  const update = () => {
    const next = readScrolled();
    if (next !== last) {
      last = next;
      onChange(next);
    }
  };

  scroller.addEventListener('scroll', update, { passive: true });
  window.addEventListener('scroll', update, { passive: true });
  update();

  return () => {
    scroller.removeEventListener('scroll', update);
    window.removeEventListener('scroll', update);
  };
}

function wallStickyHeaderHeight(scroller: HTMLElement): number {
  const toolbar = scroller.querySelector('mat-toolbar') as HTMLElement | null;
  if (!toolbar) {
    return 0;
  }
  const style = window.getComputedStyle(toolbar);
  if (style.position !== 'sticky' && style.position !== 'fixed') {
    return 0;
  }
  return toolbar.getBoundingClientRect().height || 0;
}

/** Height of other cards' comment panels above this image in the same column (open or still collapsing). */
function wallCommentsHeightAbove(media: HTMLElement): number {
  const card = media.closest('article');
  const mediaRect = media.getBoundingClientRect();
  let extra = 0;
  document.querySelectorAll('.eazix-exhibition-comments-clip').forEach((node) => {
    const clip = node as HTMLElement;
    if (card && card.contains(clip)) {
      return;
    }
    const r = clip.getBoundingClientRect();
    if (r.height < 1) {
      return;
    }
    const overlap =
      Math.min(r.right, mediaRect.right) - Math.max(r.left, mediaRect.left);
    if (overlap < Math.min(r.width || 1, mediaRect.width || 1) * 0.35) {
      return;
    }
    if (r.bottom > mediaRect.top + 4) {
      return;
    }
    extra += r.height;
  });
  return extra;
}

function wallDesiredScrollTop(
  scroller: HTMLElement,
  media: HTMLElement,
  headerH: number
): number {
  const visibleTop =
    scroller.getBoundingClientRect().top + headerH + WALL_OPEN_SCROLL_OFFSET_PX;
  const extra = wallCommentsHeightAbove(media);
  return Math.max(
    0,
    scroller.scrollTop + media.getBoundingClientRect().top - visibleTop - extra
  );
}

/** Scroll so the top of the image sits ~5px below the visible top of the wall scroller. */
export function scrollWallMediaToVisibleTop(
  media: HTMLElement | null,
  onDone?: () => void,
): void {
  if (!media || typeof window === 'undefined') {
    onDone?.();
    return;
  }
  if (wallOpenScrollRaf) {
    cancelAnimationFrame(wallOpenScrollRaf);
    wallOpenScrollRaf = 0;
  }
  const gen = ++wallOpenScrollGen;
  const finish = () => {
    if (gen !== wallOpenScrollGen) {
      return;
    }
    onDone?.();
  };
  const run = () => {
    if (gen !== wallOpenScrollGen) {
      return;
    }
    const scroller = wallScrollParent(media);
    const headerH = wallStickyHeaderHeight(scroller);
    const from = scroller.scrollTop;
    const extra = wallCommentsHeightAbove(media);
    const to = wallDesiredScrollTop(scroller, media, headerH);
    if (Math.abs(to - from) < 1) {
      finish();
      return;
    }
    const duration =
      extra > 4 ? WALL_OPEN_SCROLL_WITH_COLLAPSE_MS : WALL_OPEN_SCROLL_MS;
    const start = performance.now();
    const tick = (now: number) => {
      if (gen !== wallOpenScrollGen) {
        return;
      }
      const desired = wallDesiredScrollTop(scroller, media, headerH);
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      scroller.scrollTop = from + (desired - from) * eased;
      if (t < 1) {
        wallOpenScrollRaf = requestAnimationFrame(tick);
      } else {
        scroller.scrollTop = wallDesiredScrollTop(scroller, media, headerH);
        wallOpenScrollRaf = 0;
        finish();
      }
    };
    wallOpenScrollRaf = requestAnimationFrame(tick);
  };
  requestAnimationFrame(() => requestAnimationFrame(run));
}

function wallAnimateScrollBy(
  scroller: HTMLElement,
  delta: number,
  durationMs: number,
): void {
  const start = scroller.scrollTop;
  const target = start + delta;
  const t0 = performance.now();
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
  const step = (now: number) => {
    const p = Math.min(1, (now - t0) / Math.max(1, durationMs));
    scroller.scrollTop = start + (target - start) * easeOutCubic(p);
    if (p < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

/** Scroll an element into the visible focus / comments scroll area. */
export function scrollWallFocusElIntoView(
  el: HTMLElement | null,
  options?: { pinEnd?: boolean },
): void {
  if (!el || typeof window === 'undefined') {
    return;
  }

  const innerScroll = el.closest(
    '.eazix-exhibition-comments-clip, .eazix-exhibition-focus-scroll, .wall-card-social-scroll',
  ) as HTMLElement | null;
  const focusScroll = el.closest('.wall-item-focus-scroll') as HTMLElement | null;
  const focusPanel = el.closest(
    '.personal-wall-card-focus, .eazix-exhibition-card-focus',
  ) as HTMLElement | null;
  const clip = el.closest('.eazix-exhibition-comments-clip') as HTMLElement | null;

  let scroller: HTMLElement | null = null;
  if (innerScroll) {
    const oy = getComputedStyle(innerScroll).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      scroller = innerScroll;
    }
  }
  if (!scroller && clip) {
    const oy = getComputedStyle(clip).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      scroller = clip;
    }
  }
  if (!scroller && focusPanel) {
    const oy = getComputedStyle(focusPanel).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
      scroller = focusPanel;
    }
  }
  if (!scroller && focusScroll) {
    scroller = focusScroll;
    focusScroll.classList.add('can-scroll');
    focusScroll.style.overflowY = 'auto';
    focusScroll.dispatchEvent(
      new CustomEvent('wall-focus-ensure-scroll', { bubbles: true }),
    );
  }
  if (!scroller) {
    el.scrollIntoView({ block: 'end', behavior: 'smooth', inline: 'nearest' });
    return;
  }

  const reveal = () => {
    const pad = 20;
    const vv = window.visualViewport;
    const viewTop = vv ? vv.offsetTop : 0;
    const viewBottom = vv
      ? vv.offsetTop + vv.height
      : window.innerHeight;

    const elRect = el.getBoundingClientRect();
    const scRect = scroller!.getBoundingClientRect();
    const visibleBottom = Math.min(scRect.bottom, viewBottom) - pad;
    const visibleTop = Math.max(scRect.top, viewTop) + pad;

    let delta = 0;
    if (options?.pinEnd) {
      if (elRect.bottom > visibleBottom) {
        delta = elRect.bottom - visibleBottom;
      }
    } else if (elRect.bottom > visibleBottom) {
      delta = elRect.bottom - visibleBottom;
    } else if (elRect.top < visibleTop) {
      delta = elRect.top - visibleTop;
    }
    if (!delta) {
      return;
    }

    if (delta > 0) {
      const maxScroll = Math.max(
        0,
        scroller!.scrollHeight - scroller!.clientHeight,
      );
      const target = scroller!.scrollTop + delta;
      if (target > maxScroll + 1) {
        const need = Math.ceil(target - maxScroll + 40);
        const cur = parseFloat(getComputedStyle(scroller!).paddingBottom) || 0;
        scroller!.style.paddingBottom = `${Math.max(cur, need)}px`;
      }
      const panel = scroller!.closest(
        '.personal-wall-card-focus, .eazix-exhibition-card-focus',
      );
      const canScroll =
        !!panel &&
        (panel.classList.contains('personal-wall-card-focus-fill') ||
          panel.classList.contains('personal-wall-card-focus-body-scroll') ||
          panel.classList.contains('eazix-exhibition-card-focus-fill') ||
          panel.classList.contains('eazix-exhibition-card-focus-body-scroll') ||
          panel.classList.contains('personal-wall-card-focus-open') ||
          panel.classList.contains('eazix-exhibition-card-open'));
      if (canScroll) {
        // Never show a native scrollbar — CSS also hides it; set inline as belt+suspenders.
        scroller!.style.overflowY = 'auto';
        scroller!.style.setProperty('scrollbar-width', 'none');
        (scroller!.style as CSSStyleDeclaration & { msOverflowStyle?: string }).msOverflowStyle =
          'none';
      }
    }

    wallAnimateScrollBy(scroller!, delta, 220);
  };

  requestAnimationFrame(() => requestAnimationFrame(reveal));
}

/** Best-effort natural panel height before focus open / relayout. */
export function wallFocusNaturalPanelHeight(
  panel: HTMLElement,
  contentHeight: number,
  extraReservePx = 0,
): number {
  void panel.offsetHeight;
  // Prefer intrinsic content height. Do not use the panel's current box /
  // scrollHeight — once a load-reserve stretches the dialog, those stay tall
  // and leave a blank band after comments arrive.
  const reserveInDom = !!panel.querySelector('.eazix-exhibition-comments-reserve');
  const addReserve =
    extraReservePx > 0 && !reserveInDom ? extraReservePx : 0;
  return Math.max(1, contentHeight + addReserve);
}

/** Read decoded image pixels when the model has not caught up yet. */
export function wallFocusNatFromPanelImg(
  panel: HTMLElement | null,
  imgSelector = '.eazix-exhibition-card-img, .personal-wall-card-img'
): { natW: number; natH: number } | null {
  if (!panel) {
    return null;
  }
  const img = panel.querySelector(imgSelector) as HTMLImageElement | null;
  const natW = img?.naturalWidth || 0;
  const natH = img?.naturalHeight || 0;
  if (natW > 0 && natH > 0) {
    return { natW, natH };
  }
  return null;
}

/**
 * After programmatic scroll / layout shift, lazy images and infinite-scroll
 * sentinels may sit in view without browser events. Kick lazy imgs that are
 * near the visible scroller area.
 */
export function kickWallLazyImagesNearView(root: ParentNode | null): void {
  if (!root || typeof window === 'undefined') {
    return;
  }
  const margin = 280;
  const imgs = root.querySelectorAll('img[loading="lazy"]');
  imgs.forEach((node) => {
    const img = node as HTMLImageElement;
    const r = img.getBoundingClientRect();
    if (
      r.bottom < -margin ||
      r.top > window.innerHeight + margin ||
      r.right < -margin ||
      r.left > window.innerWidth + margin
    ) {
      return;
    }
    img.loading = 'eager';
    const src = img.currentSrc || img.src;
    if (src && !img.complete) {
      img.src = src;
    }
  });
}

/**
 * Mirror my-orders getImgPlotter / getImgExpress (printed) URL rules only.
 */
export function toPublicWallThumb(
  url?: string | null,
  opts?: { serverAddress?: string | null }
): string {
  const u = String(url || '').trim();
  if (isWallPlaceholderThumb(u)) {
    return '';
  }
  // getImgExpress local disk
  if (u.startsWith('/home/ubuntu/IMG-Express')) {
    return u.replace('/home/ubuntu/IMG-Express', EXPRESS_WALL_CDN);
  }
  // getImgPlotter local / relative (also fix `/uploads/...` if backend missed prefix)
  const serverAddress = String(opts?.serverAddress || '').replace(/\/+$/, '');
  const isPlotterLocal =
    u.startsWith('uploads') ||
    u.startsWith('/uploads') ||
    u.startsWith('/home/ubuntu/IMG-Plotter');
  if (isPlotterLocal) {
    if (!serverAddress) {
      return u.startsWith('uploads') ? '/' + u : u;
    }
    if (u.startsWith('uploads')) {
      return serverAddress + '/' + u;
    }
    if (u.startsWith('/uploads')) {
      return serverAddress + u;
    }
    return serverAddress + u.replace('/home/ubuntu/IMG-Plotter', '');
  }
  // Already public (S3 Location etc.) — same as getImgPlotter else branch
  return u;
}

/** Resolve wall thumb using API thumbUrl + printer serverAddress (my-orders rules). */
export function resolveWallItemThumb(item?: {
  thumbUrl?: string | null;
  serverAddress?: string | null;
} | null): string {
  return toPublicWallThumb(item?.thumbUrl, {
    serverAddress: item?.serverAddress,
  });
}

export interface WallPublishItemPayload {
  fileId: string;
  printingService: 'plotter' | 'express';
  page: number;
  serialName: string;
  title?: string;
  description?: string;
  fileName?: string;
  /** Clockwise degrees: 0 | 90 | 180 | 270 */
  rotation?: 0 | 90 | 180 | 270;
  /** True = publish to personal wall but exclude from public exhibition */
  hiddenFromAudience?: boolean;
}

export interface WallPublishedItem {
  _id: string;
  fileId: string;
  printingService: 'plotter' | 'express';
  page: number;
  serialName: string;
  title?: string;
  description?: string;
  thumbUrl?: string;
  /** Plotter branch host — same as order.printerID.serverAddress in my-orders */
  serverAddress?: string;
  fileName?: string;
  rotation?: 0 | 90 | 180 | 270;
  /** True = kept on personal wall, excluded from public exhibition */
  hiddenFromAudience?: boolean;
  likeCount?: number;
  commentCount?: number;
  commenterCount?: number;
  likedByMe?: boolean;
  recentLikers?: string[];
  recentCommenters?: string[];
  created?: string;
  updated?: string;
}

export interface WallItemUpdatePayload {
  title?: string;
  description?: string;
  rotation?: 0 | 90 | 180 | 270;
  hiddenFromAudience?: boolean;
}

export interface WallUnsharedPrintedItem {
  id: string;
  fileId: string;
  printingService: 'plotter' | 'express';
  page: number;
  serialName: string;
  fileName?: string;
  thumbUrl?: string;
  serverAddress?: string;
  orderId?: string;
  orderCreated?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WallApiService {
  /** Prevents starting multiple pollers in the same app session */
  private wallStatusPollingStarted = false;
  private wallStatusPollSub: Subscription | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Fetch wallStatus immediately on app open, then refresh every 5 minutes.
   * Full page reload restarts this via AppComponent.
   */
  startWallStatusPolling(): void {
    if (this.wallStatusPollingStarted) {
      return;
    }
    this.wallStatusPollingStarted = true;
    this.refreshWallStatus();
    this.wallStatusPollSub = interval(WALL_STATUS_POLL_MS).subscribe(() => {
      this.refreshWallStatus();
    });
  }

  private refreshWallStatus(): void {
    this.http
      .get<{ message: string; wallStatus: boolean }>(BACKEND_URL + 'status')
      .subscribe({
        next: (res) => {
          localStorage.setItem(
            'wallStatus',
            String(res?.wallStatus === true)
          );
        },
        error: () => {
          /* keep previous localStorage value on transient errors */
        },
      });
  }

  publishWorks(items: WallPublishItemPayload[]): Observable<{
    message: string;
    wallId: string;
    itemIds: string[];
    itemsCount: number;
  }> {
    return this.http.post<{
      message: string;
      wallId: string;
      itemIds: string[];
      itemsCount: number;
    }>(BACKEND_URL + 'publish', { items });
  }

  getMyWall(): Observable<{
    message: string;
    wall: {
      _id: string;
      about?: string;
      lookingForWork?: boolean;
      offeringHelp?: boolean;
      allowContact?: boolean;
      academySerialName?: string;
      items: WallPublishedItem[];
    } | null;
  }> {
    return this.http.get<{
      message: string;
      wall: {
        _id: string;
        about?: string;
        lookingForWork?: boolean;
        offeringHelp?: boolean;
        allowContact?: boolean;
        academySerialName?: string;
        items: WallPublishedItem[];
      } | null;
    }>(BACKEND_URL + 'me');
  }

  updateWallProfile(payload: {
    about?: string;
    lookingForWork?: boolean;
    offeringHelp?: boolean;
    allowContact?: boolean;
    academySerialName?: string;
  }): Observable<{
    message: string;
    wall: {
      _id: string;
      about: string;
      lookingForWork: boolean;
      offeringHelp: boolean;
      allowContact: boolean;
      academySerialName: string;
    };
  }> {
    return this.http.put<{
      message: string;
      wall: {
        _id: string;
        about: string;
        lookingForWork: boolean;
        offeringHelp: boolean;
        allowContact: boolean;
        academySerialName: string;
      };
    }>(BACKEND_URL + 'profile', payload);
  }

  updateItem(
    itemId: string,
    payload: WallItemUpdatePayload
  ): Observable<{ message: string; item: WallPublishedItem }> {
    return this.http.put<{ message: string; item: WallPublishedItem }>(
      BACKEND_URL + 'items/' + itemId,
      payload
    );
  }

  updateItemRotation(
    itemId: string,
    rotation: 0 | 90 | 180 | 270
  ): Observable<{ message: string; item: WallPublishedItem }> {
    return this.http.put<{ message: string; item: WallPublishedItem }>(
      BACKEND_URL + 'items/' + itemId + '/rotation',
      { rotation }
    );
  }

  deleteItem(itemId: string): Observable<{ message: string; itemId: string }> {
    return this.http.delete<{ message: string; itemId: string }>(
      BACKEND_URL + 'items/' + itemId
    );
  }

  getUnsharedPrintedFeed(
    excludeIds: string[] = [],
    limit = 30
  ): Observable<{
    message: string;
    items: WallUnsharedPrintedItem[];
    hasMore: boolean;
  }> {
    return this.http.post<{
      message: string;
      items: WallUnsharedPrintedItem[];
      hasMore: boolean;
    }>(BACKEND_URL + 'unshared/feed', { excludeIds, limit });
  }

  getExhibitionFeed(
    excludeIds: string[] = [],
    limit = 24,
    userId?: string | null,
    filters?: {
      academySerialName?: string | null;
      lookingForWork?: boolean;
      offeringHelp?: boolean;
    } | null
  ): Observable<{
    message: string;
    items: ExhibitionFeedItem[];
    hasMore: boolean;
    profile?: ExhibitionOwnerProfile;
  }> {
    const body: {
      excludeIds: string[];
      limit: number;
      userID?: string;
      academySerialName?: string;
      lookingForWork?: boolean;
      offeringHelp?: boolean;
    } = { excludeIds, limit };
    const uid = String(userId || '').trim();
    if (uid) {
      body.userID = uid;
    }
    const academy = String(filters?.academySerialName || '').trim();
    if (academy) {
      body.academySerialName = academy;
    }
    if (filters?.lookingForWork === true) {
      body.lookingForWork = true;
    }
    if (filters?.offeringHelp === true) {
      body.offeringHelp = true;
    }
    return this.http.post<{
      message: string;
      items: ExhibitionFeedItem[];
      hasMore: boolean;
      profile?: ExhibitionOwnerProfile;
    }>(BACKEND_URL + 'exhibition/feed', body);
  }

  toggleItemLike(itemId: string): Observable<{
    message: string;
    itemId: string;
    liked: boolean;
    likeCount: number;
    recentLikers?: string[];
  }> {
    return this.http.post<{
      message: string;
      itemId: string;
      liked: boolean;
      likeCount: number;
      recentLikers?: string[];
    }>(BACKEND_URL + 'items/' + itemId + '/like', {});
  }

  getItemLikes(itemId: string): Observable<{
    message: string;
    names: string[];
    likeCount: number;
  }> {
    return this.http.get<{
      message: string;
      names: string[];
      likeCount: number;
    }>(BACKEND_URL + 'items/' + itemId + '/likes');
  }

  toggleCommentLike(
    itemId: string,
    commentId: string
  ): Observable<{
    message: string;
    itemId: string;
    commentId: string;
    liked: boolean;
    likeCount: number;
    recentLikers?: string[];
  }> {
    return this.http.post<{
      message: string;
      itemId: string;
      commentId: string;
      liked: boolean;
      likeCount: number;
      recentLikers?: string[];
    }>(BACKEND_URL + 'items/' + itemId + '/comments/' + commentId + '/like', {});
  }

  getCommentLikes(
    itemId: string,
    commentId: string
  ): Observable<{
    message: string;
    names: string[];
    likeCount: number;
  }> {
    return this.http.get<{
      message: string;
      names: string[];
      likeCount: number;
    }>(BACKEND_URL + 'items/' + itemId + '/comments/' + commentId + '/likes');
  }

  getItemComments(itemId: string): Observable<{
    message: string;
    comments: ExhibitionComment[];
  }> {
    return this.http.get<{
      message: string;
      comments: ExhibitionComment[];
    }>(BACKEND_URL + 'items/' + itemId + '/comments');
  }

  addItemComment(
    itemId: string,
    text: string,
    parentId?: string
  ): Observable<{
    message: string;
    comment: ExhibitionComment;
    commentCount: number;
  }> {
    const body: { text: string; parentId?: string } = { text };
    if (parentId) {
      body.parentId = parentId;
    }
    return this.http.post<{
      message: string;
      comment: ExhibitionComment;
      commentCount: number;
    }>(BACKEND_URL + 'items/' + itemId + '/comments', body);
  }

  updateItemComment(
    itemId: string,
    commentId: string,
    text: string
  ): Observable<{
    message: string;
    comment: ExhibitionComment;
  }> {
    return this.http.put<{
      message: string;
      comment: ExhibitionComment;
    }>(BACKEND_URL + 'items/' + itemId + '/comments/' + commentId, { text });
  }

  deleteItemComment(
    itemId: string,
    commentId: string
  ): Observable<{
    message: string;
    commentId: string;
    itemId: string;
    commentCount: number;
  }> {
    return this.http.delete<{
      message: string;
      commentId: string;
      itemId: string;
      commentCount: number;
    }>(BACKEND_URL + 'items/' + itemId + '/comments/' + commentId);
  }

  sendWallContact(
    userID: string,
    text: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(BACKEND_URL + 'contact', {
      userID,
      text,
    });
  }
}

export interface ExhibitionOwnerProfile {
  userID: string;
  authorName?: string;
  authorImageURL?: string;
  authorImageCandidates?: string[];
  about?: string;
  lookingForWork?: boolean;
  offeringHelp?: boolean;
  allowContact?: boolean;
  academySerialName?: string;
}

export interface ExhibitionFeedItem {
  _id: string;
  fileId: string;
  printingService: 'plotter' | 'express';
  page: number;
  serialName: string;
  title?: string;
  description?: string;
  thumbUrl?: string;
  serverAddress?: string;
  fileName?: string;
  rotation?: 0 | 90 | 180 | 270;
  userID: string;
  authorName?: string;
  authorImageURL?: string;
  authorImageCandidates?: string[];
  academySerialName?: string;
  lookingForWork?: boolean;
  offeringHelp?: boolean;
  allowContact?: boolean;
  likeCount: number;
  commentCount: number;
  commenterCount?: number;
  likedByMe: boolean;
  recentLikers?: string[];
  recentCommenters?: string[];
  created?: string;
}

export interface ExhibitionComment {
  _id: string;
  wallItemId: string;
  userID: string;
  parentId?: string | null;
  authorName?: string;
  authorImageURL?: string;
  authorImageCandidates?: string[];
  text: string;
  created?: string;
  replies?: ExhibitionComment[];
  editing?: boolean;
  draftText?: string;
  saving?: boolean;
  confirmDelete?: boolean;
  replying?: boolean;
  draftReply?: string;
  commentingReply?: boolean;
  likeCount?: number;
  likedByMe?: boolean;
  recentLikers?: string[];
  likers?: string[];
  likersExpanded?: boolean;
  liking?: boolean;
}
